import {
  getOperationScopeKey,
  type AsyncOperation,
  type AsyncOperationSummary,
  type OperationKey,
  type OperationScope,
} from '@vrplatform/data';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useStore } from 'zustand';
import {
  OperationTrackerContext,
  OperationTrackerLifetimeContext,
  type OperationTrackerState,
} from './operationTracker.store';
import type {
  RecoverOperationsInput,
  TrackOperationGroupInput,
  TrackOperationInput,
  TrackedOperation,
  TrackedOperationGroup,
} from './operationTracker.types';

export const useOperationTrackerStore = () => {
  const store = useContext(OperationTrackerContext);
  if (!store) throw new Error('OperationTrackerProvider not found');

  return store;
};

export const useOperationTrackerLifetimeSignal = () => {
  const signal = useContext(OperationTrackerLifetimeContext);
  if (!signal) throw new Error('OperationTrackerProvider not found');

  return signal;
};

export const useTrackOperation = () => {
  const store = useOperationTrackerStore();

  return useCallback(
    (input: TrackOperationInput) => store.getState().trackOperation(input),
    [store]
  );
};

export const useOperationSubmissions = () => {
  const store = useOperationTrackerStore();

  return useStore(store, (state) => state.submissions);
};

export const useTrackedOperation = (key: OperationKey) => {
  const store = useOperationTrackerStore();

  return useStore(store, (state) => state.operations[key]);
};

const isStoppedOperation = (operation: TrackedOperation) =>
  operation.status !== 'queued' && operation.status !== 'running';

const createWaitAbortError = () =>
  new DOMException(
    'Waiting for the tracked operation was aborted',
    'AbortError'
  );

export const useWaitForTrackedOperation = () => {
  const store = useOperationTrackerStore();
  const pendingWaitsRef = useRef(new Set<() => void>());

  useEffect(
    () => () => {
      for (const abortWait of pendingWaitsRef.current) abortWait();
      pendingWaitsRef.current.clear();
    },
    []
  );

  return useCallback(
    (
      key: OperationKey,
      options: { abortOnUnmount?: boolean; signal?: AbortSignal } = {}
    ) =>
      new Promise<TrackedOperation>((resolve, reject) => {
        const current = store.getState().operations[key];
        if (!current) {
          reject(new Error('Tracked operation not found'));
          return;
        }
        if (isStoppedOperation(current)) {
          resolve(current);
          return;
        }
        if (options.signal?.aborted) {
          reject(createWaitAbortError());
          return;
        }

        let settled = false;
        let unsubscribe = () => {};
        const cleanup = () => {
          unsubscribe();
          options.signal?.removeEventListener('abort', abortWait);
          pendingWaitsRef.current.delete(abortWait);
        };
        const abortWait = () => {
          if (settled) return;
          settled = true;
          cleanup();
          reject(createWaitAbortError());
        };
        const resolveIfStopped = (operation: TrackedOperation | undefined) => {
          if (settled) return;
          if (!operation) {
            abortWait();
            return;
          }
          if (!isStoppedOperation(operation)) return;

          settled = true;
          cleanup();
          resolve(operation);
        };

        if (options.abortOnUnmount !== false) {
          pendingWaitsRef.current.add(abortWait);
        }
        options.signal?.addEventListener('abort', abortWait, { once: true });
        unsubscribe = store.subscribe((state) => {
          resolveIfStopped(state.operations[key]);
        });
        resolveIfStopped(store.getState().operations[key]);
      }),
    [store]
  );
};

const getOperationCreatedAt = (operation: TrackedOperation) => {
  if ('createdAt' in operation.operation) {
    const createdAt = Date.parse(operation.operation.createdAt);
    if (Number.isFinite(createdAt)) return createdAt;
  }

  return operation.updatedAt;
};

const isOperationForResource = (
  operation: TrackedOperation,
  scopeKey: string,
  resourceId: string,
  resourceType: NonNullable<AsyncOperationSummary['resource']>['type'],
  operationType?: AsyncOperation['type']
) =>
  getOperationScopeKey(operation.reference) === scopeKey &&
  operation.resource?.id === resourceId &&
  operation.resource.type === resourceType &&
  (!operationType || operation.operation.type === operationType);

const getNewestOperation = (
  operations: readonly TrackedOperation[]
): TrackedOperation | undefined =>
  operations.reduce<TrackedOperation | undefined>((newest, operation) => {
    if (
      !newest ||
      getOperationCreatedAt(newest) < getOperationCreatedAt(operation)
    ) {
      return operation;
    }

    return newest;
  }, undefined);

export const useTrackedOperationForResource = (
  scope: OperationScope,
  resource: NonNullable<AsyncOperationSummary['resource']>,
  operationType?: AsyncOperation['type']
) => {
  const store = useOperationTrackerStore();
  const operations = useStore(store, (state) => state.operations);
  const scopeKey = getOperationScopeKey(scope);

  return useMemo(
    () =>
      Object.values(operations).reduce<TrackedOperation | undefined>(
        (newest, operation) => {
          if (
            !isOperationForResource(
              operation,
              scopeKey,
              resource.id,
              resource.type,
              operationType
            )
          ) {
            return newest;
          }

          if (
            !newest ||
            getOperationCreatedAt(newest) < getOperationCreatedAt(operation)
          ) {
            return operation;
          }

          return newest;
        },
        undefined
      ),
    [operationType, operations, resource.id, resource.type, scopeKey]
  );
};

export const getVisibleTrackedOperationForResource = (
  operations: OperationTrackerState['operations'],
  scope: OperationScope,
  resource: NonNullable<AsyncOperationSummary['resource']>,
  operationType?: AsyncOperation['type']
) => {
  const scopeKey = getOperationScopeKey(scope);
  const matchingOperations = Object.values(operations).filter((operation) =>
    isOperationForResource(
      operation,
      scopeKey,
      resource.id,
      resource.type,
      operationType
    )
  );
  const newestActiveOperation = getNewestOperation(
    matchingOperations.filter(
      (operation) =>
        operation.status === 'queued' || operation.status === 'running'
    )
  );

  if (newestActiveOperation) return newestActiveOperation;

  const newestOperationByType = new Map<string, TrackedOperation>();
  for (const operation of matchingOperations) {
    const type = operation.operation.type;
    const newestOfType = newestOperationByType.get(type);

    if (
      !newestOfType ||
      getOperationCreatedAt(newestOfType) < getOperationCreatedAt(operation)
    ) {
      newestOperationByType.set(type, operation);
    }
  }

  return getNewestOperation(
    [...newestOperationByType.values()].filter(
      (operation) =>
        operation.status === 'failed' ||
        operation.status === 'unavailable' ||
        operation.status === 'error'
    )
  );
};

export const useVisibleTrackedOperationForResource = (
  scope: OperationScope,
  resource: NonNullable<AsyncOperationSummary['resource']>,
  operationType?: AsyncOperation['type']
) => {
  const store = useOperationTrackerStore();
  const operations = useStore(store, (state) => state.operations);

  return useMemo(
    () =>
      getVisibleTrackedOperationForResource(
        operations,
        scope,
        resource,
        operationType
      ),
    [operationType, operations, resource, scope]
  );
};

export const useActiveOperationForResource = (
  scope: OperationScope,
  resource: NonNullable<AsyncOperationSummary['resource']>,
  operationType?: AsyncOperation['type']
) => {
  const operation = useTrackedOperationForResource(
    scope,
    resource,
    operationType
  );

  return operation?.status === 'queued' || operation?.status === 'running'
    ? operation
    : undefined;
};

export const useRecoverOperations = ({
  summary,
  resolveScope,
  effectsByType,
}: RecoverOperationsInput) => {
  const store = useOperationTrackerStore();

  useEffect(() => {
    for (const operation of summary.items) {
      const scope = resolveScope(operation);
      if (!scope) continue;

      store.getState().recoverOperation({
        operation,
        scope,
        effects: effectsByType?.[operation.type],
      });
    }
  }, [effectsByType, resolveScope, store, summary.items]);
};

export const useTrackOperationGroup = () => {
  const store = useOperationTrackerStore();

  return useCallback(
    (input: TrackOperationGroupInput) =>
      store.getState().trackOperationGroup(input),
    [store]
  );
};

const getTrackedOperationGroup = (
  group: OperationTrackerState['groups'][string] | undefined,
  operations: OperationTrackerState['operations']
): TrackedOperationGroup | undefined => {
  if (!group) return undefined;

  const activeOperationKeys: OperationKey[] = [];
  const completedOperationKeys: OperationKey[] = [];
  const failedOperationKeys: OperationKey[] = [];
  const unavailableOperationKeys: OperationKey[] = [];

  for (const key of group.operationKeys) {
    const operation = operations[key];

    if (
      !operation ||
      operation.status === 'queued' ||
      operation.status === 'running'
    ) {
      activeOperationKeys.push(key);
    } else if (operation.status === 'completed') {
      completedOperationKeys.push(key);
    } else if (operation.status === 'failed') {
      failedOperationKeys.push(key);
    } else {
      unavailableOperationKeys.push(key);
    }
  }

  const state = activeOperationKeys.length
    ? 'active'
    : failedOperationKeys.length
      ? 'partialFailure'
      : unavailableOperationKeys.length
        ? 'unavailable'
        : 'completed';

  return {
    ...group,
    state,
    activeOperationKeys,
    completedOperationKeys,
    failedOperationKeys,
    unavailableOperationKeys,
  };
};

export const useTrackedOperationGroup = (groupId: string) => {
  const store = useOperationTrackerStore();
  const group = useStore(store, (state) => state.groups[groupId]);
  const operations = useStore(store, (state) => state.operations);

  return useMemo(
    () => getTrackedOperationGroup(group, operations),
    [group, operations]
  );
};

export { getTrackedOperationGroup };
